// Public page reached from the landing-page "Request Access" CTA. Lets a
// prospective user submit name, email, organization, and a short reason.
// Hits POST /api/public/access-request — the same endpoint the admin's
// "Access Requests" panel reads from. Route: /request-access (App.jsx).
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Loader2,
  Mail,
  User,
  Building2,
  MessageSquare,
  AlertCircle,
} from 'lucide-react'
import toast from 'react-hot-toast'
import AuthBrandPanel from '../components/auth/AuthBrandPanel'
// The same three claims AuthShell shows, now genuinely the same array rather
// than a second copy that had already drifted ("district" here, "sector"
// there). The icons came out of the lucide import above with it.
import { AUTH_BRAND_ITEMS } from '../components/auth/authBrandCopy'

export default function RequestAccessPage() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    organization: '',
    reason: '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState(null)
  const reduceMotion = useReducedMotion()

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  const submit = async (e) => {
    e.preventDefault()
    setError(null)
    if (!form.name.trim() || !form.email.trim()) {
      setError('Name and email are required.')
      return
    }
    setSubmitting(true)
    try {
      const res = await fetch('/api/public/access-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          organization: form.organization.trim() || undefined,
          reason: form.reason.trim() || undefined,
        }),
      })
      const body = await res.json().catch(() => ({}))
      if (!res.ok || body?.success === false) {
        throw new Error(body?.error || `Submission failed (HTTP ${res.status})`)
      }
      setSubmitted(true)
      toast.success('Request submitted')
    } catch (err) {
      setError(err?.message || 'Could not reach the IEWRS server. Try again later.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-viewport lg:h-viewport flex flex-col lg:flex-row bg-white lg:overflow-hidden">
      {/* Form column — containerless, same treatment as AuthShell.jsx. */}
      <div className="flex-1 flex items-center justify-center px-4 py-10 sm:px-6 lg:px-12 lg:overflow-y-auto lg:order-2">
        <div className="w-full max-w-[420px]">
          <div className="mb-6">
            <Link
              to="/"
              className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900 transition link-focus rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
              Back to IEWRS
            </Link>
          </div>

          <motion.div
            initial={reduceMotion ? undefined : { opacity: 0, y: 16 }}
            animate={reduceMotion ? undefined : { opacity: 1, y: 0 }}
            transition={reduceMotion ? undefined : { duration: 0.4, ease: 'easeOut' }}
          >
            <AnimatePresence mode="wait">
                {/* SUCCESS STATE */}
                {submitted && (
                  <motion.div
                    key="success"
                    initial={reduceMotion ? undefined : { opacity: 0, y: 8 }}
                    animate={reduceMotion ? undefined : { opacity: 1, y: 0 }}
                    transition={reduceMotion ? undefined : { duration: 0.3, ease: 'easeOut' }}
                    className="text-center"
                  >
                    <div className="w-14 h-14 rounded-full bg-success-100 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-success-600" strokeWidth={1.75} />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Thanks, {form.name.split(' ')[0] || 'we appreciate it'}.
                    </h2>
                    <p className="text-sm text-gray-600 mt-2 max-w-md mx-auto">
                      Your request has been submitted. An IEWRS administrator will review it
                      and email you at <span className="font-mono">{form.email}</span> when a
                      decision is made.
                    </p>
                    <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                      <Link
                        to="/"
                        className="btn-secondary btn-md"
                      >
                        Back to home
                      </Link>
                      <Link
                        to="/login"
                        className="btn-brand btn-md"
                      >
                        Already have access? Sign in
                      </Link>
                    </div>
                  </motion.div>
                )}

                {/* FORM STATE */}
                {!submitted && (
                  <motion.form
                    key="form"
                    onSubmit={submit}
                    initial={reduceMotion ? undefined : { opacity: 0 }}
                    animate={reduceMotion ? undefined : { opacity: 1 }}
                    transition={reduceMotion ? undefined : { duration: 0.25 }}
                    noValidate
                  >
                    <div className="mb-6 text-center">
                      <h2 className="text-xl font-semibold tracking-tight text-gray-900">
                        Request access
                      </h2>
                      <p className="mt-1 text-sm text-gray-600">
                        We usually reply within a few working days.
                      </p>
                    </div>

                    {error && (
                      <div className="flex items-start gap-2 alert-danger mb-5" role="alert">
                        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.75} />
                        <span className="text-sm">{error}</span>
                      </div>
                    )}

                    <div className="space-y-5">
                      {/* Name + organization pair; email and reason need the full row. */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <label htmlFor="ra-name" className="block">
                          <span className="field-label">Full name *</span>
                          <div className="mt-2 relative">
                            <User className="input-icon" strokeWidth={1.75} />
                            <input
                              id="ra-name"
                              type="text"
                              value={form.name}
                              onChange={update('name')}
                              disabled={submitting}
                              required
                              aria-required="true"
                              className="input pl-11"
                              placeholder="Jane Mukamana"
                            />
                          </div>
                        </label>

                        <label htmlFor="ra-organization" className="block">
                          <span className="field-label">
                            Organization <span className="normal-case font-normal text-gray-400">(optional)</span>
                          </span>
                          <div className="mt-2 relative">
                            <Building2 className="input-icon" strokeWidth={1.75} />
                            <input
                              id="ra-organization"
                              type="text"
                              value={form.organization}
                              onChange={update('organization')}
                              disabled={submitting}
                              className="input pl-11"
                              placeholder="e.g. Ministry of Health"
                            />
                          </div>
                        </label>
                      </div>

                      <label htmlFor="ra-email" className="block">
                        <span className="field-label">Email *</span>
                        <div className="mt-2 relative">
                          <Mail className="input-icon" strokeWidth={1.75} />
                          <input
                            id="ra-email"
                            type="email"
                            value={form.email}
                            onChange={update('email')}
                            disabled={submitting}
                            required
                            aria-required="true"
                            className="input pl-11"
                            placeholder="jane@institute.gov.rw"
                          />
                        </div>
                        <p className="field-hint">
                          We&apos;ll reply here when your request has been reviewed.
                        </p>
                      </label>

                      <label htmlFor="ra-reason" className="block">
                        <span className="field-label">
                          Why do you need access? <span className="normal-case font-normal text-gray-400">(optional)</span>
                        </span>
                        <div className="mt-2 relative">
                          <MessageSquare className="input-icon-top" strokeWidth={1.75} />
                          <textarea
                            id="ra-reason"
                            value={form.reason}
                            onChange={update('reason')}
                            disabled={submitting}
                            rows={3}
                            className="input pl-11 resize-y"
                            placeholder="One or two sentences on how you'll use IEWRS data — research, public health response, education, etc."
                          />
                        </div>
                      </label>

                      <div>
                        <button
                          type="submit"
                          disabled={submitting}
                          className="btn-brand btn-lg w-full gap-2"
                        >
                          {submitting ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.75} />
                              Submitting&hellip;
                            </>
                          ) : (
                            <>
                              Submit access request
                              <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
                            </>
                          )}
                        </button>
                        <p className="text-xs text-gray-500 text-center mt-3">
                          No account is created yet — an administrator reviews every request
                          before access is granted.
                        </p>
                      </div>

                      <p className="text-xs text-gray-500 text-center">
                        Already have an account?{' '}
                        <Link to="/login" className="text-primary-700 hover:text-primary-800 font-medium">
                          Sign in
                        </Link>
                      </p>
                    </div>
                  </motion.form>
                )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>

      {/* Same AuthBrandPanel the login/register/forgot surfaces render via
          AuthShell — this page hand-rolls its own layout but shares the panel. */}
      <AuthBrandPanel
        heading="Get access to IEWRS"
        subtitle="Tell us a little about yourself and we'll set you up with role-based access to Rwanda's climate-health early warning platform."
        items={AUTH_BRAND_ITEMS}
      />
    </div>
  )
}
